import React from 'react';
import styled, { keyframes } from 'styled-components';
import { colors } from '../../styles/colors';

const subtleFlow = keyframes`
  0% { background-position: 0% 50%; }
  100% { background-position: 100% 50%; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const PageContainer = styled.div`
  min-height: 100vh;
  background: url('/images/background.jpg') center/cover no-repeat fixed;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 1;
    background: ${colors.gradientBlue};
    opacity: 0.88;
    background-size: 200% 200%;
    animation: ${subtleFlow} 15s linear infinite alternate;
    will-change: background-position;
  }
`;

export const ContentBox = styled.div`
  background: ${colors.primary};
  padding: 2.5rem;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1);
  width: 100%;
  max-width: 800px;
  z-index: 2;
  position: relative;
  animation: ${fadeIn} 0.8s ease-out;
  border: 1px solid rgba(255, 255, 255, 0.1);
  max-height: 90vh;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 2rem;
    margin: 20px;
    border-radius: 10px;
    max-height: 85vh;
  }

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 10px;

    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

export const Title = styled.h1`
  color: ${colors.textPrimary};
  font-size: 2rem;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

export const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  color: ${colors.textPrimary};
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const Section = styled.section`
  margin-bottom: 2rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const SectionTitle = styled.h2`
  color: ${colors.textPrimary};
  background: ${colors.sidebarBg};
  font-size: 1.15rem;
  font-weight: 700;
  font-family: 'Montserrat', sans-serif;
  margin-bottom: 1rem;
  padding: 0.65rem 1rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const Text = styled.p`
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
  margin-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const TextWithTopMargin = styled(Text)`
  margin-top: 1rem;
`;

export const TextNoMargin = styled(Text)`
  margin: 0;
`;

export const FooterText = styled(Text)`
  text-align: center;
  margin-top: 2rem;
  color: ${colors.secondaryBlue};
`;

export const ContactInfo = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 1.5rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

export const List = styled.ul`
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.8;
  padding-left: 1.5rem;

  li {
    margin-bottom: 0.5rem;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

export const HighlightBox = styled.div`
  background: rgba(1, 1, 77, 0.35);
  border-left: 3px solid ${colors.sidebarBg};
  padding: 1rem 1.5rem;
  border-radius: 0 8px 8px 0;
  margin: 1rem 0;
`;

export const LastUpdated = styled.div`
  text-align: center;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
`;

export const AccentLink = styled.a`
  color: ${colors.textPrimary};
  background: ${colors.sidebarBg};
  padding: 0.12rem 0.45rem;
  border-radius: 4px;
  text-decoration: none;
  font-weight: 600;

  &:hover {
    background: ${colors.primaryDark};
    color: ${colors.textPrimary};
  }
`;

export const LabeledListItem = ({ label, text }) => (
  <li>
    <strong style={{ color: colors.textPrimary, background: colors.sidebarBg, padding: '0.1rem 0.35rem', borderRadius: 4 }}>
      {label}
    </strong>
    : {text}
  </li>
);

export default function PublicLegalPageLayout({ title, backLabel, onBack, children }) {
  return (
    <PageContainer>
      <ContentBox>
        <Header>
          <Title>{title}</Title>
          <BackButton type="button" onClick={onBack}>
            {backLabel}
          </BackButton>
        </Header>
        {children}
      </ContentBox>
    </PageContainer>
  );
}
